import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Grid, TextField, Typography,
} from '@mui/material';

const DEFAULT_URL = 'https://websiteownership.dioem.cloud';

const WebsiteOwnershipConfigCard = ({
  config, isLoading, saveMutation, testPending, onTest, onMessage,
}) => {
  const [form, setForm] = useState({
    baseUrl: DEFAULT_URL, token: '', timeoutMs: 120000, batchSize: 100,
  });

  useEffect(() => {
    if (!config) return;
    setForm({
      baseUrl: config.baseUrl || DEFAULT_URL,
      token: '',
      timeoutMs: config.timeoutMs || 120000,
      batchSize: config.batchSize || 100,
    });
  }, [config]);

  const update = (event) => setForm((current) => ({
    ...current,
    [event.target.name]: event.target.value,
  }));

  const save = () => saveMutation.mutate({
    ...form,
    timeoutMs: Number(form.timeoutMs),
    batchSize: Number(form.batchSize),
  }, {
    onSuccess: () => onMessage({ message: 'Website auditor configuration saved.', severity: 'success' }),
    onError: (error) => onMessage({
      message: error.response?.data?.message || 'Failed to save Website auditor configuration.',
      severity: 'error',
    }),
  });

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Website Ownership Auditor</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Stores the service URL and encrypted bearer token used by the Website ownership scheduler.
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <Alert severity={config?.configured ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {config?.configured ? 'Auditor credential is configured.' : 'Auditor credential is not configured.'}
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Auditor URL" name="baseUrl" value={form.baseUrl} onChange={update} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Bearer Token" name="token" type="password" value={form.token}
                  placeholder={config?.tokenSet ? 'Leave blank to keep current token' : 'Enter bearer token'}
                  onChange={update}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Request Timeout (ms)" name="timeoutMs" type="number" value={form.timeoutMs} onChange={update} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Batch Size" name="batchSize" type="number" value={form.batchSize} onChange={update} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button data-testid="website-auditor-save" variant="contained" disabled={saveMutation.isPending} onClick={save}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outlined" disabled={!config?.configured || testPending} onClick={onTest}>
                {testPending ? 'Testing…' : 'Test Connection'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WebsiteOwnershipConfigCard;
